import { Footer } from "@/components/Footer";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, CheckCircle2, Code, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import newRelicLogo from "@/assets/newrelic-logo.png";
import { WaitlistCTA } from "@/components/WaitlistCTA";

const NewRelic = () => {

  return (
    <main className="min-h-screen">
      <ThemeToggle />
      
      {/* Header */}
      <section className="py-8 px-4 border-b border-border">
        <div className="container mx-auto max-w-6xl">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back to Home
          </Link>
        </div>
      </section>

      {/* Hero */}
      <section className="py-16 sm:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
                Fix New Relic Bugs with Healion
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Convert New Relic insights into automated code fixes with AI-powered analysis and instant PR creation
              </p>
            </div>
            <div className="relative h-[300px] rounded-xl overflow-hidden bg-background border border-border flex items-center justify-center p-8">
              <img src={newRelicLogo} alt="New Relic Logo" className="w-full h-full object-contain dark:invert" />
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-12 text-center">How It Works</h2>
          
          <div className="space-y-8">
            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-accent">1</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold mb-2">New Relic Observability</h3>
                  <p className="text-muted-foreground">
                    New Relic provides full-stack observability, tracking application performance, errors, and user experiences in real-time.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-accent">2</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold mb-2">Alert Webhook Trigger</h3>
                  <p className="text-muted-foreground">
                    When an alert condition is met, New Relic sends a webhook to Healion with incident details, error traces, and transaction data.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-accent">3</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold mb-2">Smart Prioritization</h3>
                  <p className="text-muted-foreground">
                    Healion analyzes alert priority, affected users, and service impact to determine which issues need immediate fixes.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-accent">4</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold mb-2">AI-Powered Resolution</h3>
                  <p className="text-muted-foreground">
                    The AI agent correlates error analytics with your codebase to identify the exact issue and generate a production-ready fix.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-accent">5</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold mb-2">PR with Analytics Context</h3>
                  <p className="text-muted-foreground">
                    A pull request is created with the fix, including New Relic dashboard links and performance impact analysis.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Setup Instructions */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-12 text-center">Setup Integration</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Code className="w-5 h-5 text-accent" />
                Step 1: Configure Webhook Destination
              </h3>
              <Card className="p-6 bg-muted/50">
                <ol className="space-y-3 list-decimal list-inside text-muted-foreground">
                  <li>Log into New Relic and navigate to Alerts & AI → Destinations</li>
                  <li>Click "Create destination" and select "Webhook"</li>
                  <li>Name it "Healion" and enter the endpoint URL:</li>
                </ol>
                <div className="mt-4 p-4 bg-background rounded-md font-mono text-sm border border-border">
                  https://api.healion.ai/webhooks/newrelic
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  Authentication: <span className="font-semibold">None</span> (Healion validates via signature)
                </p>
              </Card>
            </div>

            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-accent" />
                Step 2: Create Workflow
              </h3>
              <Card className="p-6 bg-muted/50">
                <ol className="space-y-3 list-decimal list-inside text-muted-foreground">
                  <li>Go to Alerts & AI → Workflows</li>
                  <li>Click "Create workflow"</li>
                  <li>Choose your trigger (e.g., "Issue created")</li>
                  <li>Select condition filters (priority, tags, etc.)</li>
                  <li>Add the "Healion" webhook destination as the action</li>
                  <li>Configure notification details and activate workflow</li>
                </ol>
              </Card>
            </div>

            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-accent" />
                Step 3: Test & Validate
              </h3>
              <Card className="p-6 bg-muted/50">
                <p className="text-muted-foreground mb-4">
                  Verify the integration is working correctly:
                </p>
                <ul className="space-y-2 list-disc list-inside text-muted-foreground">
                  <li>Trigger a test alert in New Relic</li>
                  <li>Confirm webhook delivery in workflow activity log</li>
                  <li>Check Healion dashboard for received incident</li>
                  <li>Verify PR is created with fix and New Relic context</li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-12 text-center">Key Benefits</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6">
              <CheckCircle className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">Performance-Aware Fixes</h3>
              <p className="text-muted-foreground">
                AI considers APM data and transaction traces when generating fixes.
              </p>
            </Card>

            <Card className="p-6">
              <CheckCircle className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">Business Impact Analysis</h3>
              <p className="text-muted-foreground">
                Prioritize fixes based on user impact metrics from New Relic analytics.
              </p>
            </Card>

            <Card className="p-6">
              <CheckCircle className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">Reduce MTTR</h3>
              <p className="text-muted-foreground">
                Dramatically reduce mean time to resolution with automated fixes.
              </p>
            </Card>

            <Card className="p-6">
              <CheckCircle className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">Unified Observability</h3>
              <p className="text-muted-foreground">
                Keep New Relic as your source of truth while Healion handles fixes.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-muted-foreground mb-8">
            Connect New Relic to Healion and automate your incident resolution
          </p>
          
          <WaitlistCTA source="New Relic Page" layout="stacked" />
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default NewRelic;